import { RefObject } from 'preact';

/**
 * Default Redwood line-height overrides cause false positives as the Range sizes are always
 * for a 'normal' line-height, and none of the default scale fonts have 'normal' line-height
 * values. This causes offsets of between 0.5px and 1.5px. As a true truncation will cause
 * an offset of closer to a full line height (at least 12px for 2xs text), use a larger offset
 * value than the currently observed 1.5px max to reduce the likelihood of false positives.
 */
const verticalTruncationOffset = 5;

/**
 * On Chrome it appears that sometimes there is a very slight difference between the
 * untruncatedText.width and the renderedText.width on the order of 0.00000[more numbers].
 * It only seems to happen for certain strings, so it might be related to font rendering.
 * In any case, in order to address this we use a horizontalTruncationOffset
 * to reduce false positives.
 */
const horizontalTruncationOffset = 1;

/**
 * Returns true if the size of an element's node contents exceeds the size
 * of the element itself.
 * @param rootRef An element ref
 */
export const detectTruncation = (rootRef: RefObject<HTMLElement>) => {
  if (rootRef.current) {
    const element = rootRef.current;
    const range = new Range();
    range.selectNodeContents(element);
    const untruncatedText = range.getBoundingClientRect();
    const renderedText = element.getBoundingClientRect();
    const computedStyles = getComputedStyle(element);
    const padding =
      parseFloat(computedStyles.paddingLeft) + parseFloat(computedStyles.paddingRight);
    const border = parseFloat(computedStyles.borderLeft) + parseFloat(computedStyles.borderRight);
    const truncated =
      untruncatedText.width > renderedText.width - padding - border + horizontalTruncationOffset ||
      untruncatedText.height > renderedText.height + verticalTruncationOffset;
    return truncated;
  }
  return false;
};
